import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner
} from 'reactstrap';
import { ProductImageData } from '../../types/productImageTypes';

interface ImageViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageData: ProductImageData | null;
}

const ImageViewModal: React.FC<ImageViewModalProps> = ({ isOpen, onClose, imageData }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Construct the image URL
  const getImageUrl = (imageName: string): string => {
    const baseUrl = 'https://sf.appws.qdos.tech/RADEDITOR/App/Client_Images/UAT_ClientImages/OurBusiness';
    return `${baseUrl}/${imageName}`;
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Reset states when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [isOpen, imageData?.image]);

  if (!imageData) {
    return null;
  }

  const imageUrl = getImageUrl(imageData.image);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg" className="image-view-modal">
      <ModalHeader toggle={onClose} className="border-bottom">
        <div className="d-flex flex-column">
          <h5 className="modal-title mb-1">{imageData.name || 'Product Image'}</h5>
          <small className="text-muted">
            Image #{imageData.imageNumber} • {imageData.type || 'N/A'}
            {imageData.primary && <span className="badge bg-primary ms-2">Primary</span>}
          </small>
        </div>
      </ModalHeader>
      
      <ModalBody className="p-0">
        <div className="position-relative d-flex justify-content-center align-items-center" 
             style={{ minHeight: '400px', backgroundColor: '#f8f9fa' }}>
          
          {/* Loading Spinner */}
          {isLoading && (
            <div className="position-absolute d-flex flex-column align-items-center">
              <Spinner color="primary" size="sm" className="mb-2" />
              <small className="text-muted">Loading image...</small>
            </div>
          )}

          {/* Error State */}
          {hasError && !isLoading && (
            <div className="position-absolute d-flex flex-column align-items-center text-center p-4">
              <i className="ri-image-line fs-1 text-muted mb-3"></i>
              <h6 className="text-muted mb-2">Failed to load image</h6>
              <small className="text-muted">
                The image could not be loaded. Please check if the file exists.
              </small>
              <small className="text-muted mt-2">
                <strong>File:</strong> {imageData.image}
              </small>
            </div>
          )}

          {/* Image */}
          <img
            src={imageUrl}
            alt={imageData.name || 'Product image'}
            className={`img-fluid ${isLoading || hasError ? 'd-none' : ''}`}
            style={{ 
              maxHeight: '70vh', 
              maxWidth: '100%',
              objectFit: 'contain'
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        </div>

        {/* Image Details */}
        {!isLoading && !hasError && (
          <div className="border-top bg-light p-3">
            <div className="row g-3">
              <div className="col-md-6">
                <small className="text-muted d-block">Image File</small>
                <span className="fw-medium">{imageData.image}</span>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Order</small>
                <span className="fw-medium">{imageData.order || 0}</span>
              </div>
              {imageData.comments && (
                <div className="col-12">
                  <small className="text-muted d-block">Comments</small>
                  <span className="fw-medium">{imageData.comments}</span>
                </div>
              )}
              <div className="col-md-6">
                <small className="text-muted d-block">Created</small>
                <span className="fw-medium">
                  {imageData.created ? new Date(imageData.created).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Created By</small>
                <span className="fw-medium">{imageData.createdName || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="border-top">
        <div className="d-flex justify-content-between w-100">
          <div className="d-flex align-items-center">
            {!hasError && (
              <small className="text-muted">
                <i className="ri-external-link-line me-1"></i>
                <a 
                  href={imageUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  Open in new tab
                </a>
              </small>
            )}
          </div>
          <div>
            <Button color="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ImageViewModal;